<template>
  <div class="pm-settings">
    <div class="settings-wrapper">
      <div v-if="activeTab === 'task_statuses'">
        <div class="d-flex justify-content-between mb-3 align-items-center">
            <div>
                <h6 class="mb-0">{{ $t('task_statuses') }}</h6>
                <p class="text-muted small mb-0">{{ $t('manage_task_statuses_desc') }}</p>
            </div>
            <button class="btn btn-sm btn-outline-primary" @click="addStatus">
              <AppIcon name="bi-plus" class="bi bi-plus me-1" />{{$t('add_status')}}
            </button>
        </div>

        <div class="px-4 pb-4 mb-6 bg-white rounded shadow">
            
            <div v-if="loading" class="text-center" style="padding:1rem">{{$t('loading')}}...</div>
            
            <div v-if="!loading" class="mt-4">
                <div class="small text-muted mb-3">
                    <AppIcon name="bi-info-circle" class="bi bi-info-circle me-1" />
                    {{ $t('task_statuses_info') }}
                </div>

                <!-- Draggable Status List -->
                <div v-for="(status, index) in editStatuses" :key="status.id || index" 
                     class="d-flex align-items-center gap-3 mb-3 status-row"
                     :class="{ 'opacity-50': !status.enabled }">
                  
                  <!-- Drag Handle -->
                  <div class="drag-handle text-muted" style="cursor: grab; width: 20px;">
                    <AppIcon name="bi-grip-vertical" class="bi bi-grip-vertical" />
                  </div>

                  <!-- Enable/Disable Toggle -->
                  <div style="width: 40px;">
                      <div class="form-check form-switch">
                          <input 
                              class="form-check-input" 
                              type="checkbox" 
                              :id="'status-toggle-' + index"
                              v-model="status.enabled"
                              :disabled="isRequiredStatus(status)"
                          >
                      </div>
                  </div>

                  <!-- Color Picker -->
                  <div style="width: 50px;">
                      <input type="color" v-model="status.color" class="form-control form-control-color w-100" style="height: 38px; cursor: pointer;">
                  </div>

                  <!-- Status Key (internal, read-only for defaults) -->
                  <div style="width: 120px;">
                      <input 
                          type="text" 
                          v-model="status.value" 
                          class="form-control form-control-sm" 
                          :placeholder="$t('status_key')"
                          :disabled="status.isDefault"
                          style="font-family: monospace; font-size: 12px;"
                      >
                  </div>

                  <!-- Status Display Name -->
                  <div class="flex-grow-1">
                      <input type="text" v-model="status.label" class="form-control" :placeholder="$t('status_name')">
                  </div>

                  <!-- Preview Badge -->
                  <div style="min-width: 80px;">
                      <span class="badge" :style="{ backgroundColor: status.color, color: getContrastColor(status.color) }">
                          {{ status.label || status.value }}
                      </span>
                  </div>

                  <!-- Delete -->
                  <div>
                      <button 
                          class="btn btn-outline-danger btn-sm" 
                          @click="removeStatus(index)" 
                          :disabled="status.isDefault"
                          :title="status.isDefault ? $t('cannot_delete_default_status') : $t('delete')"
                      >
                        <AppIcon name="bi-trash" class="bi bi-trash" />
                      </button>
                  </div>
                </div>

                <div class="mt-4 d-flex justify-content-between align-items-center">
                    <button type="button" class="btn btn-outline-secondary btn-sm" @click="resetToDefaults">
                      <AppIcon name="bi-arrow-counterclockwise" class="bi bi-arrow-counterclockwise me-1" />{{$t('reset_to_defaults')}}
                    </button>
                    <button type="button" class="btn btn-success" :disabled="saving" @click="saveStatuses">
                      <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
                      <AppIcon name="bi-check-lg" v-else class="bi bi-check-lg me-1" />{{$t('save')}}
                    </button>
                </div>
            </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
import helper from '../../helper';

const DEFAULT_STATUSES = [
  { value: 'todo', label: 'To Do', color: '#6c757d', enabled: true, isDefault: true },
  { value: 'in_progress', label: 'In Progress', color: '#0d6efd', enabled: true, isDefault: true },
  { value: 'review', label: 'Review', color: '#ffc107', enabled: true, isDefault: true },
  { value: 'completed', label: 'Completed', color: '#198754', enabled: true, isDefault: true },
  { value: 'cancelled', label: 'Cancelled', color: '#dc3545', enabled: true, isDefault: true }
];

export default {
  name: 'ProjectManagementSettings',
  props: ['activeTab'],
  data() {
    return {
      editStatuses: [],
      loading: false,
      saving: false
    };
  },
  mounted() {
    if (this.activeTab === 'task_statuses') {
        this.loadStatuses();
    }
  },
  watch: {
    activeTab(newTab) {
        if (newTab === 'task_statuses') {
            this.loadStatuses();
        }
    }
  },
  methods: {
    async loadStatuses() {
      if (helper.checkAccess('edit_tasks') <= 0) return;
      try {
        this.loading = true;
        const res = await helper.customGet(`${process.env.VUE_APP_API}/project-management/task-statuses`);
        const statuses = res.data.task_statuses || [];
        
        if (statuses.length) {
          this.editStatuses = statuses.map(s => ({
            ...s,
            id: s.id || 'status_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5)
          }));
        } else {
          this.editStatuses = JSON.parse(JSON.stringify(DEFAULT_STATUSES)).map(s => ({
            ...s,
            id: 'status_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5)
          }));
        }
        this.loading = false;
      } catch (e) { 
        this.loading = false;
      }
    },
    addStatus() {
      if (helper.checkAccess('edit_tasks') <= 0) return;
      const id = 'status_' + Date.now();
      this.editStatuses.push({ 
        id, 
        value: '', 
        label: '', 
        color: '#6c757d', 
        enabled: true, 
        isDefault: false 
      });
    },
    removeStatus(index) {
      if (helper.checkAccess('edit_tasks') <= 0) return;
      if (this.editStatuses[index].isDefault) return;
      this.editStatuses.splice(index, 1);
    },
    isRequiredStatus(status) {
      // 'todo' must always be enabled as it's the default status for new tasks
      return status.value === 'todo';
    },
    resetToDefaults() {
      if (helper.checkAccess('edit_tasks') <= 0) return;
      this.editStatuses = JSON.parse(JSON.stringify(DEFAULT_STATUSES)).map(s => ({
        ...s,
        id: 'status_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5)
      }));
    },
    getContrastColor(hexColor) {
      if (!hexColor) return '#fff';
      const hex = hexColor.replace('#', '');
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      return luminance > 0.5 ? '#000' : '#fff';
    },
    async saveStatuses() {
      if (helper.checkAccess('edit_tasks') <= 0) return;
      try {
        this.saving = true;
        
        // Validate: at least 'todo' must exist and be enabled
        const todoStatus = this.editStatuses.find(s => s.value === 'todo');
        if (!todoStatus || !todoStatus.enabled) {
          helper.showToast(this.$t('todo_status_required'), 'error');
          this.saving = false;
          return;
        }

        // Validate: no empty values
        const invalidStatus = this.editStatuses.find(s => !s.value.trim() || !s.label.trim());
        if (invalidStatus) {
          helper.showToast(this.$t('status_fields_required'), 'error');
          this.saving = false;
          return;
        }

        // Validate: no duplicate values
        const values = this.editStatuses.map(s => s.value.trim().toLowerCase());
        const hasDuplicates = values.length !== new Set(values).size;
        if (hasDuplicates) {
          helper.showToast(this.$t('duplicate_status_keys'), 'error');
          this.saving = false;
          return;
        }
        
        const taskStatuses = this.editStatuses.map((s, index) => ({
          value: s.value.trim().toLowerCase().replace(/\s+/g, '_'),
          label: s.label.trim(),
          color: s.color,
          enabled: s.enabled,
          isDefault: s.isDefault || false,
          position: index
        }));

        const res = await helper.customPatch(`${process.env.VUE_APP_API}/project-management/task-statuses`, {
          task_statuses: taskStatuses
        }, { showToast: true });
        
        const business = { ...this.$store.getters.BUSINESS };
        business.metadata = {
          ...(business.metadata || {}),
          task_statuses: res.data.task_statuses
        };
        this.$store.commit('SET_BUSINESS', business);

        this.saving = false;
        this.loadStatuses();
      } catch (e) {
        this.saving = false;
      }
    }
  }
};
</script>

<style scoped>
.pm-settings {
  padding: 0;
}
.settings-wrapper {
  max-height: calc(80vh - 120px);
  overflow-y: auto;
  overflow-x: hidden;
}
.status-row {
  padding: 8px 12px;
  border-radius: 6px;
  transition: all 0.2s ease;
}
.status-row:hover {
  background-color: #f8f9fa;
}
.drag-handle:hover {
  color: #333 !important;
}
</style>
